import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import queryString from 'query-string';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ProFormaCapTableModel } from 'api';
import { captableAction } from 'common/actions';
import { notFoundPath } from 'common/config/urls';
import { CAP_TABLE_SLUG } from 'common/constants/cap-table/capTable';
import { useStore } from 'common/store';
import { CapTableVersion } from 'common/types/company';
import { UseStoreValues } from 'common/types/store';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { COMPANY_PAGES } from 'layouts/Main/components/Sidebar/constants';
import { IMeasurementDate } from 'models/interfaces/measurement-date.interface';
import { useCreateCapTableProforma, useGetCaptableInfo } from 'services/hooks/captable';
import { useGetVersionsByCompanyIdByDateId } from 'services/hooks/company';
import useGenerateChildrenCompaniesCapTablesVersions from './useGenerateChildrenCompaniesCapTablesVersions';
import { RelatedEntity } from '../../../ValuationsAllocation/types';

const useCapTableVersionFilter = ({
  selectedMeasurementDate,
  onAddRelatedEntity,
}: {
  selectedMeasurementDate: IMeasurementDate;
  onAddRelatedEntity: () => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const parsedQuery = queryString.parse(location.search);
  const { enqueueSnackbar } = useSnackbar();
  const { tableSlugParam } = useParams();

  // Route-based navigation: active tab is the current route segment.
  const activeTabId = tableSlugParam || CAP_TABLE_SLUG;

  const activeTabIdRef = useRef(activeTabId);
  const isOnRestrictedPageRef = useRef(false);

  useEffect(() => {
    activeTabIdRef.current = activeTabId;
  }, [activeTabId]);

  const [{ firmId, companyInfo, captableInfo, companyList, companyMeasurementDateList }, dispatch] = useStore() as unknown as UseStoreValues;
  const companyId = companyInfo?.id;
  const selectedMeasurementDateId = selectedMeasurementDate?.id;
  const selectedDate = selectedMeasurementDate?.date;
  const currentVersionsKey = useMemo(
    () => (companyId && selectedMeasurementDateId ? `${companyId}-${selectedMeasurementDateId}` : null),
    [companyId, selectedMeasurementDateId]
  );

  const { action: hasChanges, showDialog } = useContext(UnsavedChangesContext);

  const getCapTableVersions = useGetVersionsByCompanyIdByDateId();
  const [capTableVersions, setCapTableVersions] = useState<CapTableVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string | number | null>();
  const [newProforma, setProformaInfo, createProforma, isCreatingProforma] = useCreateCapTableProforma() as unknown as [
    ProFormaCapTableModel & { slug: string },
    (payload: ProFormaCapTableModel) => void,
    (payload: ProFormaCapTableModel) => Promise<void>,
    boolean,
  ];
  const [capTableVersionsByCompany, setCapTableVersionsByCompany] = useState<Record<string, CapTableVersion[]>>({});

  const [, getCapTableInfo] = useGetCaptableInfo() as unknown as [
    (id: number) => Promise<void>,
    (id: number) => Promise<void>,
  ];

  const { generateChildrenCompaniesCapTablesVersions } = useGenerateChildrenCompaniesCapTablesVersions(
    companyList,
    selectedDate,
    companyId
  );

  // Check if current tab restricts proformas
  const restrictedTabsMap = useMemo(() => {
    const map = new Map<string, string>();
    COMPANY_PAGES.filter(page => page.restrictProformas).forEach(page => {
      map.set(page.tab || page.slug, page.title);
    });
    return map;
  }, []);
  const isOnRestrictedPage = restrictedTabsMap.has(activeTabId);
  const hasForcedPrimaryOnRestrictedPageRef = useRef(false);
  const capTableVersionsKeyRef = useRef<string | null>(null);
  const latestCapTableVersionsKeyRef = useRef<string | null>(null);
  // Tracks the request key currently in flight (not just a boolean) so a fetch for a
  // newly-selected measurement date is never blocked by a still-pending fetch for a
  // different (now-stale) date. Staleness itself is handled separately via
  // latestCapTableVersionsKeyRef checks below.
  const fetchingKeyRef = useRef<string | null>(null);

  useEffect(() => {
    isOnRestrictedPageRef.current = isOnRestrictedPage;
  }, [isOnRestrictedPage]);

  useEffect(() => {
    const previousKey = latestCapTableVersionsKeyRef.current;
    latestCapTableVersionsKeyRef.current = currentVersionsKey;

    if (previousKey && previousKey !== currentVersionsKey) {
      dispatch(captableAction.setCaptableInfo({}));
    }

    if (capTableVersionsKeyRef.current && capTableVersionsKeyRef.current !== currentVersionsKey) {
      capTableVersionsKeyRef.current = null;
      setCapTableVersions([]);
      setCapTableVersionsByCompany({});
      setProformaInfo({} as ProFormaCapTableModel);
      setSelectedVersion(null);
      hasForcedPrimaryOnRestrictedPageRef.current = false;
    }
  }, [currentVersionsKey, dispatch, setProformaInfo]);

  const fetchCapTableVersions = useCallback(async () => {
    if (!companyId || !selectedMeasurementDateId || !companyInfo) {
      return;
    }

    const requestKey = `${companyId}-${selectedMeasurementDateId}`;

    // Don't fetch if this request is already stale before we start
    if (requestKey !== currentVersionsKey) {
      return;
    }

    // Don't fetch if already fetching for this exact key. A fetch for a different
    // (now-stale) key must NOT block this one — the staleness checks below already
    // discard results for keys that are no longer current.
    if (fetchingKeyRef.current === requestKey) {
      return;
    }

    // Guard: wait until the current company's measurement dates are loaded and the
    // selected date is one of them before calling the cap-table versions API.
    if (!companyMeasurementDateList?.some(md => md.id === selectedMeasurementDateId)) {
      return;
    }

    fetchingKeyRef.current = requestKey;

    try {
      const fetchedCapTableVersions = (await getCapTableVersions(
        companyId,
        selectedMeasurementDateId,
        true
      )) as (CapTableVersion & { id: number })[];

      // Check again after async operation completes
      if (requestKey !== latestCapTableVersionsKeyRef.current) {
        return;
      }

      const sortedCapTableVersions = fetchedCapTableVersions?.toSorted((a, b) => a.id - b.id) ?? [];

      let tempCapTableVersionsByCompany: Record<string, CapTableVersion[]> = {
        [companyInfo.name]: sortedCapTableVersions,
      };

      // Check for child companies in both companyInfo and companyList
      // companyList is updated immediately after creating a related entity, while companyInfo may lag
      const companyFromList = companyList?.find(c => c.company_id === companyId);
      const hasChildCompanies =
        (companyInfo?.child_companies && companyInfo.child_companies.length > 0) ||
        (companyFromList?.child_companies && companyFromList.child_companies.length > 0);

      if (hasChildCompanies) {
        const childCompanyCapTableVersions = await generateChildrenCompaniesCapTablesVersions();
        tempCapTableVersionsByCompany = { ...tempCapTableVersionsByCompany, ...childCompanyCapTableVersions };
      }

      // Final check before updating state
      if (requestKey !== latestCapTableVersionsKeyRef.current) {
        return;
      }
      capTableVersionsKeyRef.current = requestKey;
      setCapTableVersionsByCompany(tempCapTableVersionsByCompany);
      setCapTableVersions(sortedCapTableVersions);
    } finally {
      // Only clear if this fetch is still the one "in flight" for its key — a newer
      // fetch for a different key may have already taken over fetchingKeyRef.
      if (fetchingKeyRef.current === requestKey) {
        fetchingKeyRef.current = null;
      }
    }
  }, [
    companyId,
    selectedMeasurementDateId,
    currentVersionsKey,
    getCapTableVersions,
    generateChildrenCompaniesCapTablesVersions,
    companyInfo,
    companyList,
    companyMeasurementDateList,
  ]);

  const setQueryString = useCallback(
    (paramName: string, newQueryString: string, replace = true) => {
      const currentLocation = window.location;
      const parsed = queryString.parse(currentLocation.search);
      parsed[paramName] = newQueryString;

      const newLocation = {
        pathname: currentLocation.pathname,
        search: queryString.stringify(parsed),
      };

      return replace ? navigate(newLocation, { replace: true }) : navigate(newLocation);
    },
    [navigate]
  );

  const handleVersionQueryString = async (
    versionArg: string,
    capTableVersionsArg: CapTableVersion[] = capTableVersions
  ) => {
    const { version: versionQueryString } = parsedQuery;
    const version = versionArg || versionQueryString;
    const { [companyInfo!.name]: primaryCompanyVersions, ...childCompanyMapping } = capTableVersionsByCompany;
    const primaryVersion = capTableVersionsArg.find(ct => ct.is_primary) ?? capTableVersionsArg[0];
    const companyFromList = companyList?.find(c => c.company_id === companyId);

    const matchVersion = (versionSlug: string) =>
      capTableVersionsArg.find(item => item.slug === versionSlug || item?.id?.toString() === versionSlug.toString());
    const shouldFetchCaptableInfo = (versionId?: string | number | null) => {
      if (!versionId || !captableInfo?.id) {
        return true;
      }
      return captableInfo.id.toString() !== versionId.toString();
    };

    const applyVersionSelection = async (matchedVersion: CapTableVersion, skipFetch = false) => {
      if (versionQueryString !== matchedVersion.slug) {
        setQueryString('version', matchedVersion.slug as string);
      }
      if (selectedVersion?.toString() !== matchedVersion?.id?.toString()) {
        setSelectedVersion(matchedVersion.id);
      }
      if (!skipFetch && shouldFetchCaptableInfo(matchedVersion.id)) {
        await getCapTableInfo(matchedVersion.id as number);
      }
    };

    const selectPrimaryCapTable = async () => {
      try {
        if (primaryVersion) {
          await applyVersionSelection(primaryVersion);
          return;
        }

        if (companyInfo?.cap_table_slug) {
          setQueryString('version', companyInfo.cap_table_slug as string);
          await handleVersionQueryString(companyInfo.cap_table_slug, capTableVersionsArg);
        }
      } catch (error: any) {
        throw new Error(error);
      }
    };

    try {
      // this is where we need to find out that the version is a child company version
      if (version) {
        const matchedVersion = matchVersion(version as string);

        if (matchedVersion) {
          await applyVersionSelection(matchedVersion);
        } else {
          // Find the childCompany that has this version
          const entries = Object.entries(childCompanyMapping);

          const { relatedEntity, version: versionFromChildCompany } = entries.reduce(
            (acc, [company, versions]) => {
              const foundVersion = versions.find(v => v.slug === version || v?.id?.toString() === version.toString());
              if (foundVersion) {
                const companyDetails =
                  (companyInfo?.child_companies as RelatedEntity[])?.find(c => c.company_name === company)
                  || (companyFromList?.child_companies as RelatedEntity[])?.find(c => c.company_name === company);
                return { relatedEntity: companyDetails!, version: foundVersion };
              }
              return acc;
            },
            { relatedEntity: null, version: null } as {
              relatedEntity: RelatedEntity | null;
              version: CapTableVersion | null;
            }
          );

          if (versionFromChildCompany) {
            if (!relatedEntity?.measurement_date_slug) {
              return;
            }
            if (shouldFetchCaptableInfo(versionFromChildCompany.id)) {
              await getCapTableInfo(versionFromChildCompany.id as number);
            }
            const { measurement_date_slug: measurementDateSlug } = relatedEntity as RelatedEntity;

            setQueryString('date', measurementDateSlug);
            setQueryString('version', versionFromChildCompany.slug as string);

            if (selectedVersion?.toString() !== versionFromChildCompany?.id?.toString()) {
              setSelectedVersion(versionFromChildCompany.id);
            }

            return;
          }

          if (primaryVersion) {
            await applyVersionSelection(primaryVersion);
            return;
          }

          navigate(notFoundPath);
        }
      } else if (companyInfo?.firm_id === firmId && companyInfo?.cap_table_slug) {
        await selectPrimaryCapTable();
      }
      setProformaInfo({} as ProFormaCapTableModel);
    } catch (error: any) {
      throw new Error(error);
    }
  };

  useEffect(() => {
    if (!currentVersionsKey || capTableVersionsKeyRef.current !== currentVersionsKey) {
      return;
    }

    if (!isEmpty(capTableVersions)) {
      const currentVersion = parsedQuery?.version?.toString();
      const primaryCaptable = capTableVersions.find(ct => ct.is_primary);
      const primarySlug = primaryCaptable?.slug ?? companyInfo?.cap_table_slug;

      const allVersions = !isEmpty(capTableVersionsByCompany)
        ? Object.values(capTableVersionsByCompany).flatMap(versions => versions)
        : capTableVersions;

      if (isOnRestrictedPage) {
        const matchVersion = (value?: string | number | null) =>
          value
            ? allVersions.find(item => item.slug === value || item?.id?.toString() === value.toString())
            : null;

        const matchedSelectedVersion = matchVersion(selectedVersion);
        const matchedCurrentVersion = matchVersion(currentVersion);
        const activeVersion = matchedSelectedVersion || matchedCurrentVersion;

        const companyEntry = activeVersion
          ? Object.entries(capTableVersionsByCompany).find(([, versions]) =>
            versions.some(
              version =>
                version.slug === activeVersion.slug ||
                version?.id?.toString() === activeVersion?.id?.toString()
            )
          )
          : null;

        const primaryForCompany = companyEntry?.[1]?.find(ct => ct.is_primary) || primaryCaptable;
        const targetVersion = activeVersion?.is_primary ? activeVersion : primaryForCompany;

        const shouldShowRestrictedToast = Boolean(
          activeVersion
          && !activeVersion.is_primary
          && !hasForcedPrimaryOnRestrictedPageRef.current
        );

        if (shouldShowRestrictedToast) {
          hasForcedPrimaryOnRestrictedPageRef.current = true;
          const pageName = restrictedTabsMap.get(activeTabId);
          enqueueSnackbar(
            `${pageName || 'This page'} is only viewable using the Primary Cap Table, Switching...`,
            {
              variant: 'info',
              autoHideDuration: 5000,
            }
          );
        }

        if (targetVersion?.slug) {
          const shouldSyncVersion =
            selectedVersion?.toString() !== targetVersion.id?.toString() ||
            currentVersion?.toString() !== targetVersion.slug?.toString();

          if (shouldSyncVersion) {
            handleVersionQueryString(targetVersion.slug as string, capTableVersions);
          }
        } else if (primarySlug) {
          handleVersionQueryString(primarySlug as string, capTableVersions);
        }
        return;
      }

      // Leaving restricted pages: allow future redirects/toasts again
      hasForcedPrimaryOnRestrictedPageRef.current = false;

      const isValidSelectedVersion = !!allVersions.find(ct => ct?.id?.toString() === selectedVersion?.toString());

      const version = isValidSelectedVersion
        ? selectedVersion
        : newProforma?.id || parsedQuery?.version || primaryCaptable?.slug || null;

      handleVersionQueryString(version as string, capTableVersions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capTableVersions, capTableVersionsByCompany, activeTabId, currentVersionsKey]);

  const saveAsNewProforma = useCallback(
    async (payload: ProFormaCapTableModel) => {
      await createProforma(payload);
    },
    [createProforma]
  );

  useEffect(() => {
    if (!currentVersionsKey || capTableVersionsKeyRef.current !== currentVersionsKey) {
      return;
    }

    const hasRequiredData = !isEmpty(companyInfo) && !isEmpty(capTableVersionsByCompany);
    const relatedEntityAdded =
      companyInfo?.child_companies?.length !== Object.keys(capTableVersionsByCompany).length - 1;
    const shouldUpdateCapTableVersions = hasRequiredData && relatedEntityAdded;

    if (shouldUpdateCapTableVersions) {
      fetchCapTableVersions();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo]);

  // get cap table versions when the selected measurement date or the company's measurement date list changes
  useEffect(() => {
    // Only fetch if we have valid, matching data and haven't fetched for this key yet
    if (
      selectedMeasurementDate &&
      currentVersionsKey &&
      capTableVersionsKeyRef.current !== currentVersionsKey
    ) {
      fetchCapTableVersions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeasurementDate, currentVersionsKey, companyMeasurementDateList]);

  const updateCapTableVersions = useCallback(
    async (proformaId: number) => {
      if (proformaId && companyInfo && selectedMeasurementDate) {
        // Update Cap Table versions
        await fetchCapTableVersions();

        setSelectedVersion(proformaId);
        setQueryString('version', newProforma.slug, false);
      }
    },
    [setQueryString, companyInfo, selectedMeasurementDate, newProforma.slug, fetchCapTableVersions]
  );

  useEffect(() => {
    if (!isEmpty(newProforma)) {
      updateCapTableVersions(newProforma.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newProforma]);

  const versionName = useMemo(() => {
    if (isEmpty(capTableVersionsByCompany) || !selectedVersion) {
      return '';
    }

    const allVersions = !isEmpty(capTableVersionsByCompany)
      ? Object.values(capTableVersionsByCompany).flatMap(versions => versions)
      : capTableVersions;
    const result = allVersions.find(option => option?.id?.toString() === selectedVersion?.toString());

    return isEmpty(result) ? '' : result.name;
  }, [capTableVersionsByCompany, capTableVersions, selectedVersion]);

  const onEditCaptable = async () => {
    if (!isEmpty(captableInfo)) {
      await fetchCapTableVersions();
    }
  };

  // Ref to track pending selection of a newly created related entity's cap table
  const pendingNewRelatedEntityRef = useRef<string | null>(null);
  // Ref to track previous companyList length to detect when new child is added
  const prevCompanyListChildCountRef = useRef<number | null>(null);
  // Ref to limit retry attempts when the new related entity's cap tables are not ready yet
  const pendingNewRelatedEntityAttemptsRef = useRef(0);
  // Ref to always have access to latest handleVersionQueryString without causing effect re-runs
  const handleVersionQueryStringRef = useRef(handleVersionQueryString);
  useEffect(() => {
    handleVersionQueryStringRef.current = handleVersionQueryString;
  });

  const onVersionSelect = useCallback(
    (versionSlugOrId: string) => {
      const callback = async () => {
        try {
          await handleVersionQueryStringRef.current(versionSlugOrId);
        } catch (error: any) {
          throw new Error(error);
        }
      };

      if (hasChanges) {
        showDialog(callback);
      } else {
        callback();
      }
    },
    [hasChanges, showDialog]
  );

  // Effect to detect when companyList is updated with a new child company and trigger fetch
  useEffect(() => {
    if (!pendingNewRelatedEntityRef.current || !companyList || !companyId) {
      return;
    }

    const currentCompany = companyList.find(c => c.company_id === companyId);
    const childCount = currentCompany?.child_companies?.length ?? 0;

    // Check if companyList now has the new child company
    const hasNewChild = currentCompany?.child_companies?.some(
      child => child.company_name === pendingNewRelatedEntityRef.current
    );

    if (hasNewChild && childCount !== prevCompanyListChildCountRef.current) {
      prevCompanyListChildCountRef.current = childCount;
      // Trigger fetch now that companyList has been updated
      fetchCapTableVersions();
    }
  }, [companyList, companyId, fetchCapTableVersions]);

  // Effect to select the new related entity's cap table after versions are refreshed
  useEffect(() => {
    if (pendingNewRelatedEntityRef.current && !isEmpty(capTableVersionsByCompany)) {
      const newCompanyName = pendingNewRelatedEntityRef.current;
      const newCompanyVersions = capTableVersionsByCompany[newCompanyName];

      if (newCompanyVersions && newCompanyVersions.length > 0) {
        // Select the primary cap table, or the first one if no primary
        const primaryVersion = newCompanyVersions.find(v => v.is_primary) || newCompanyVersions[0];
        if (primaryVersion?.slug) {
          handleVersionQueryStringRef.current(primaryVersion.slug);
        }
        // Clear the pending selection
        pendingNewRelatedEntityRef.current = null;
        pendingNewRelatedEntityAttemptsRef.current = 0;
        return;
      }

      // If the new related entity exists but versions are not ready yet, retry a few times.
      if (pendingNewRelatedEntityAttemptsRef.current < 3) {
        pendingNewRelatedEntityAttemptsRef.current += 1;
        setTimeout(() => {
          fetchCapTableVersions();
        }, 500);
      }
    }
  }, [capTableVersionsByCompany, fetchCapTableVersions]);

  /**
   * Refreshes cap table versions and optionally selects a newly created related entity's cap table
   * @param newCompanyName - If provided, will select the primary cap table for this company after refresh
   */
  const refreshAndSelectNewRelatedEntity = useCallback(
    (newCompanyName?: string) => {
      // Store the pending selection so the effect can pick it up after companyList updates
      if (newCompanyName) {
        pendingNewRelatedEntityRef.current = newCompanyName;
        pendingNewRelatedEntityAttemptsRef.current = 0;
        // Record current child count to detect when the update arrives
        const currentCompany = companyList?.find(c => c.company_id === companyId);
        prevCompanyListChildCountRef.current = currentCompany?.child_companies?.length ?? 0;
      }
      // Don't call fetchCapTableVersions immediately - the effect watching companyList will trigger it
      // once React re-renders with the updated companyList that includes the new child company
    },
    [companyList, companyId]
  );

  return {
    saveAsNewProforma,
    isCreatingProforma,
    hasLoadedCapTableVersions: Boolean(currentVersionsKey && capTableVersionsKeyRef.current === currentVersionsKey),
    versionName,
    selectedVersion,
    onEditCaptable,
    updateCapTableVersions,
    capTableVersionsByCompany,
    capTableVersions,
    onAddRelatedEntity,
    onVersionSelect,
    refreshCapTableVersions: refreshAndSelectNewRelatedEntity,
  };
};

export default useCapTableVersionFilter;
